import { colors } from '../styles/theme/colors';
import { PRIMARY_CTA_GRADIENT, PRIMARY_CTA_GRADIENT_HOVER } from '../styles/theme/gradients';

/**
 * Apertura unificada de ventanas de impresión (popup acotado, no pestaña).
 * Abrir siempre en el mismo tick del click del usuario para evitar bloqueo de popups.
 */

export type PrintWindowSize = 'ticket' | 'document' | 'labels';

export interface OpenPrintWindowOptions {
  /** Documento HTML completo (incluye <html>…</html>). */
  html: string;
  size?: PrintWindowSize;
  /** Si true, dispara window.print() tras un breve delay. */
  autoPrint?: boolean;
  autoPrintDelayMs?: number;
  /** Nombre de ventana reutilizable (mismo popup si ya está abierto). */
  windowName?: string;
  /** Pre-opened window (from openBlankPrintWindow) so async fetch does not lose the user gesture. */
  targetWindow?: Window | null;
}

/** Mensaje cuando el navegador bloquea el popup de impresión. */
export const PRINT_POPUP_BLOCKED_MESSAGE =
  'El navegador bloqueó la ventana de impresión. Permita ventanas emergentes e intente de nuevo.';

/** Escapa entidades HTML para interpolar valores dinámicos en documentos de impresión. */
export function escapeHtml(text: string | number | undefined | null): string {
  if (text === null || text === undefined) return '';
  const str = String(text);
  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  };
  return str.replace(/[&<>"']/g, (m) => map[m]);
}

const SIZE_DIMENSIONS: Record<PrintWindowSize, { width: number; height: number }> = {
  ticket: { width: 480, height: 720 },
  document: { width: 800, height: 900 },
  labels: { width: 700, height: 800 },
};

const BASE_FEATURES = 'menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=yes';

/** Color sólido de respaldo (cyan CTA). Preferir degradado vía buildPrintChromeHtml. */
export const PRINT_ACTION_COLOR = colors.info.main;

/** CSS mínimo para ocultar chrome al imprimir. */
export const PRINT_NO_PRINT_CSS = `
  @media print {
    .no-print { display: none !important; }
  }
`;

/** Botones Imprimir / Cerrar compartidos en todas las vistas de impresión. */
export function buildPrintChromeHtml(primaryBackground: string = PRIMARY_CTA_GRADIENT): string {
  return `
    <div class="no-print" style="text-align:center;margin-top:20px;">
      <button type="button" onclick="window.print()" style="padding:10px 20px;background:${primaryBackground};color:white;border:none;border-radius:8px;cursor:pointer;font-weight:600;" onmouseover="this.style.background='${PRIMARY_CTA_GRADIENT_HOVER}'" onmouseout="this.style.background='${primaryBackground}'">Imprimir</button>
      <button type="button" onclick="window.close()" style="padding:10px 20px;background:#e5e7eb;color:#374151;border:none;border-radius:8px;cursor:pointer;margin-left:10px;font-weight:600;">Cerrar</button>
    </div>
  `;
}

function buildFeatureString(size: PrintWindowSize): string {
  const { width, height } = SIZE_DIMENSIONS[size];
  const left = Math.max(0, Math.round((window.screen.availWidth - width) / 2));
  const top = Math.max(0, Math.round((window.screen.availHeight - height) / 2));
  return `width=${width},height=${height},left=${left},top=${top},${BASE_FEATURES}`;
}

/**
 * Opens a blank print popup synchronously (must run in the same tick as a user click).
 * Write HTML later with writePrintWindow / openPrintWindow({ targetWindow }).
 */
export function openBlankPrintWindow(options?: {
  size?: PrintWindowSize;
  windowName?: string;
}): Window | null {
  const size = options?.size ?? 'document';
  const windowName = options?.windowName ?? 'printPreview';
  return window.open('', windowName, buildFeatureString(size));
}

/** Writes HTML into an already-open print window. */
export function writePrintWindow(
  printWindow: Window,
  html: string,
  options?: Pick<OpenPrintWindowOptions, 'autoPrint' | 'autoPrintDelayMs'>,
): void {
  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.focus();

  if (options?.autoPrint) {
    const delay = options.autoPrintDelayMs ?? 250;
    setTimeout(() => {
      try {
        printWindow.print();
      } catch {
        // Ignore if window was closed before print
      }
    }, delay);
  }
}

/**
 * Abre un popup de impresión dimensionado.
 * @returns la Window abierta, o null si el navegador bloqueó el popup.
 */
export function openPrintWindow(options: OpenPrintWindowOptions): Window | null {
  const size = options.size ?? 'document';
  const windowName = options.windowName ?? 'printPreview';
  const printWindow = options.targetWindow ?? window.open('', windowName, buildFeatureString(size));

  if (!printWindow) {
    return null;
  }

  writePrintWindow(printWindow, options.html, options);
  return printWindow;
}
